import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { Grid, Autocomplete, TextField, CircularProgress, Box, Typography, Stack, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from 'src/hooks/useAuth';
import axios, { endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';

export default function SubmissionLocationSelector({ projectId, isPublic }) {
  const theme = useTheme();
  const { user } = useAuth();
  const { watch, setValue } = useFormContext();

  // isPublicForm means the current user is ANONYMOUS (not logged in).
  // Even if the form has isPublic=true (allows anonymous submissions),
  // logged-in org members MUST still see the working location selector for rights filtering.
  const isAnonymousUser = !user;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const submissionState = watch('submissionState');
  const submissionCity = watch('submissionCity');
  const submissionBlock = watch('submissionBlock');
  const submissionLocality = watch('submissionLocality');

  // Loading states
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [loadingLocalities, setLoadingLocalities] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);

  // User and Project profile data
  const [userProfile, setUserProfile] = useState(null);
  const [projectProfile, setProjectProfile] = useState(null);

  // Master lists
  const [allStates, setAllStates] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [blocksList, setBlocksList] = useState([]);
  const [localitiesList, setLocalitiesList] = useState([]);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      if (isAnonymousUser || !user?.orgUserId) return;
      try {
        setLoadingUser(true);
        const res = await axios.get(endpoints.users.getById(user.orgUserId));
        if (res.data?.success) {
          setUserProfile(res.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, [user?.orgUserId, isAnonymousUser]);

  // Fetch project profile on projectId change
  useEffect(() => {
    const fetchProject = async () => {
      if (isAnonymousUser || !projectId) return;
      try {
        setLoadingProject(true);
        const res = await axios.get(endpoints.project.details(projectId));
        if (res.data?.success) {
          setProjectProfile(res.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch project details:', err);
      } finally {
        setLoadingProject(false);
      }
    };
    fetchProject();
  }, [projectId, isAnonymousUser]);

  // Fetch all system states for Admin
  useEffect(() => {
    const fetchStates = async () => {
      if (isAnonymousUser || !isAdmin) return;
      try {
        setLoadingStates(true);
        const res = await axios.get(`${endpoints.stateMaster.list}?status=active&limit=0`);
        setAllStates(res.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch master states:', err);
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, [isAdmin, isAnonymousUser]);

  // Determine eligible States
  const eligibleStates = useMemo(() => {
    if (isAnonymousUser) return [];

    let states = [];
    if (isAdmin) {
      states = allStates;
    } else {
      states = userProfile?.positioningStates || [];
    }

    // Filter by project states if configured
    if (projectProfile?.states && projectProfile.states.length > 0) {
      const projectStateIds = projectProfile.states.map(s => String(s._id || s));
      states = states.filter(s => projectStateIds.includes(String(s._id)));
    }

    return states;
  }, [isAdmin, allStates, userProfile, projectProfile, isAnonymousUser]);

  // Auto-select state if exactly one option
  useEffect(() => {
    if (eligibleStates.length === 1 && !submissionState) {
      const singleState = eligibleStates[0];
      setValue('submissionState', singleState._id);
    }
  }, [eligibleStates, submissionState, setValue]);

  // Fetch Cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (isAnonymousUser || !submissionState) {
        setCitiesList([]);
        return;
      }
      try {
        setLoadingCities(true);
        const res = await axios.get(`${endpoints.cityMaster.list}?status=active&limit=0&state=${submissionState}`);
        setCitiesList(res.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch cities:', err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [submissionState, isAnonymousUser]);

  // Filter eligible Cities
  const eligibleCities = useMemo(() => {
    if (isAnonymousUser) return [];

    let cities = citiesList;

    // For non-admin, filter by user positioning cities
    if (!isAdmin) {
      const userCityIds = (userProfile?.positioningCities || []).map(c => String(c._id || c));
      cities = cities.filter(c => userCityIds.includes(String(c._id)));
    }

    // Filter by project cities if configured
    if (projectProfile?.cities && projectProfile.cities.length > 0) {
      const projectCityIds = projectProfile.cities.map(c => String(c._id || c));
      cities = cities.filter(c => projectCityIds.includes(String(c._id)));
    }

    return cities;
  }, [isAdmin, citiesList, userProfile, projectProfile, isAnonymousUser]);

  // Auto-select city if exactly one option
  useEffect(() => {
    if (eligibleCities.length === 1 && !submissionCity) {
      const singleCity = eligibleCities[0];
      setValue('submissionCity', singleCity._id);
    }
  }, [eligibleCities, submissionCity, setValue]);

  // Fetch Blocks when city changes
  useEffect(() => {
    const fetchBlocks = async () => {
      if (isAnonymousUser || !submissionCity) {
        setBlocksList([]);
        return;
      }
      try {
        setLoadingBlocks(true);
        const res = await axios.get(`${endpoints.blockMaster.list}?status=active&limit=0&city=${submissionCity}`);
        setBlocksList(res.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch blocks:', err);
      } finally {
        setLoadingBlocks(false);
      }
    };
    fetchBlocks();
  }, [submissionCity, isAnonymousUser]);

  // Filter eligible Blocks
  const eligibleBlocks = useMemo(() => {
    if (isAnonymousUser) return [];

    let blocks = blocksList;

    // For non-admin, filter by user positioning blocks
    if (!isAdmin) {
      const userBlockIds = (userProfile?.positioningBlocks || []).map(b => String(b._id || b));
      blocks = blocks.filter(b => userBlockIds.includes(String(b._id)));
    }

    return blocks;
  }, [isAdmin, blocksList, userProfile, isAnonymousUser]);

  // Auto-select block if exactly one option
  useEffect(() => {
    if (eligibleBlocks.length === 1 && !submissionBlock) {
      const singleBlock = eligibleBlocks[0];
      setValue('submissionBlock', singleBlock._id);
    }
  }, [eligibleBlocks, submissionBlock, setValue]);

  // Fetch Localities when block changes
  useEffect(() => {
    const fetchLocalities = async () => {
      if (isAnonymousUser || !submissionBlock) {
        setLocalitiesList([]);
        return;
      }
      try {
        setLoadingLocalities(true);
        const res = await axios.get(`${endpoints.localityMaster.list}?status=active&limit=0&block=${submissionBlock}`);
        setLocalitiesList(res.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch localities:', err);
      } finally {
        setLoadingLocalities(false);
      }
    };
    fetchLocalities();
  }, [submissionBlock, isAnonymousUser]);

  // Filter eligible Localities
  const eligibleLocalities = useMemo(() => {
    if (isAnonymousUser) return [];

    let localities = localitiesList;

    // For non-admin, filter by user positioning localities
    if (!isAdmin) {
      const userLocalityIds = (userProfile?.positioningLocalities || []).map(l => String(l._id || l));
      localities = localities.filter(l => userLocalityIds.includes(String(l._id)));
    }

    return localities;
  }, [isAdmin, localitiesList, userProfile, isAnonymousUser]);

  // Auto-select locality if exactly one option
  useEffect(() => {
    if (eligibleLocalities.length === 1 && !submissionLocality) {
      const singleLocality = eligibleLocalities[0];
      setValue('submissionLocality', singleLocality._id);
    }
  }, [eligibleLocalities, submissionLocality, setValue]);

  // Hide for anonymous users (truly unauthenticated — public link without login)
  if (isAnonymousUser) return null;

  // Hide selector if the project has no states configured, matching the mobile app's projectConfiguresLocations logic
  if (projectProfile && (!projectProfile.states || projectProfile.states.length === 0)) {
    return null;
  }

  const currentSelectedState = eligibleStates.find(s => s._id === submissionState) || null;
  const currentSelectedCity = eligibleCities.find(c => c._id === submissionCity) || null;
  const currentSelectedBlock = eligibleBlocks.find(b => b._id === submissionBlock) || null;
  const currentSelectedLocality = eligibleLocalities.find(l => l._id === submissionLocality) || null;

  return (
    <Box
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.08),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Iconify icon="solar:map-point-bold-duotone" width={22} color="primary.main" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Submission Working Location
        </Typography>
        <Typography variant="caption" color="text.secondary">
          (Configures target zone for data rights)
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        {/* State */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={eligibleStates}
            loading={loadingUser || loadingProject || loadingStates}
            getOptionLabel={(option) => option.state_name || ''}
            value={currentSelectedState}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            onChange={(_, newValue) => {
              setValue('submissionState', newValue ? newValue._id : null);
              setValue('submissionCity', null);
              setValue('submissionBlock', null);
              setValue('submissionLocality', null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {(loadingUser || loadingProject || loadingStates) ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* City */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={eligibleCities}
            loading={loadingCities}
            getOptionLabel={(option) => option.city_name || ''}
            value={currentSelectedCity}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            disabled={!submissionState}
            onChange={(_, newValue) => {
              setValue('submissionCity', newValue ? newValue._id : null);
              setValue('submissionBlock', null);
              setValue('submissionLocality', null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                required
                placeholder={!submissionState ? 'Select State first' : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Block */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={eligibleBlocks}
            loading={loadingBlocks}
            getOptionLabel={(option) => option.block_name || ''}
            value={currentSelectedBlock}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            disabled={!submissionCity}
            onChange={(_, newValue) => {
              setValue('submissionBlock', newValue ? newValue._id : null);
              setValue('submissionLocality', null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Block"
                placeholder={!submissionCity ? 'Select City first' : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingBlocks ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Locality */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={eligibleLocalities}
            loading={loadingLocalities}
            getOptionLabel={(option) => option.locality_name || ''}
            value={currentSelectedLocality}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            disabled={!submissionBlock}
            onChange={(_, newValue) => {
              setValue('submissionLocality', newValue ? newValue._id : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Locality"
                placeholder={!submissionBlock ? 'Select Block first' : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocalities ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

SubmissionLocationSelector.propTypes = {
  projectId: PropTypes.string,
  isPublic: PropTypes.bool,
};
